import { useEffect } from 'react';

import useForm from 'pages/components/Form/useForm';
import FormFields from 'pages/components/FormFields';

import { Grid2 } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { UserProfile } from 'Domain/User/Entity/UserProfile';
import DebtorFormSections from './FormSections/index';
import { FormFieldsConfig } from 'pages/components/Form/FormEntities';
import Countries from 'pages/config/Countries';
import { DebtorRequest } from 'Domain/User/Dto/DebtorRequest';
import { createDebtor, updateDebtor } from 'Application/User/DebtorService';
import { useNavigate } from 'react-router-dom';
import { getUrl, DEBTORS_LIST } from 'Infrastructure/config/routesConfig';

type CreateUpdateDebtorFormProps = {
  debtorProfile?: UserProfile;
};

export default function CreateUpdateDebtorForm({
  debtorProfile,
}: CreateUpdateDebtorFormProps) {
  const isCreate = !debtorProfile;

  const ProfileFormConfig = getProfileFromConfig(debtorProfile);

  const {
    errors,
    values,
    options,
    handleInputChange,
    isValidForm,
    formatErrorObject,
    Form,
  } = useForm(ProfileFormConfig, true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const navigate = useNavigate();

  function handleSubmit(e: any) {
    e.preventDefault();
    if (isValidForm()) {
      const debtorRequest = getDebtorRequest(values);
      if (isCreate) {
        createDebtor(debtorRequest).then((res) => {
          if (!('error' in res)) {
            navigate(getUrl(DEBTORS_LIST));
          }
        });
      } else {
        updateDebtor(debtorProfile.publicId, debtorRequest);
      }
    }
  }

  useEffect(() => {
    if (values.externalIdentifier) {
      handleInputChange({
        target: {
          name: 'externalIdentifier',
          value: values.externalIdentifier,
        },
      });
    }
  }, [values.country]);

  const gridStyle = isMobile
    ? {
        container: '1fr',
      }
    : true || isCreate
      ? {
          container: 'repeat(3, 1fr)',
          references: '6 / 2 / 7 / 4',
          personal: '1 / 1 / 7 / 2',
          location: '1 / 2 / 6 / 3',
          contact: '1 / 3 / 6 / 4',
        }
      : {
          container: 'repeat(3, 1fr)',
          references: '6 / 2 / 7 / 3',
          personal: '1 / 1 / 7 / 2',
          location: '1 / 2 / 6 / 3',
          contact: '1 / 3 / 6 / 4;',
          limit: '6 / 3 / 7 / 4',
        };

  return (
    <Form onSubmit={handleSubmit}>
      <Grid2
        display={'grid'}
        gridTemplateColumns={gridStyle.container}
        gap={2}
        container
      >
        <Grid2 gridArea={gridStyle.personal}>
          <DebtorFormSections.PersonalInfos
            values={values}
            handleInputChange={handleInputChange}
            options={options}
            formatErrorObject={formatErrorObject}
            isCreate={isCreate}
            errors={errors}
          />
        </Grid2>

        <Grid2 gridArea={gridStyle.location}>
          <DebtorFormSections.Location
            values={values}
            handleInputChange={handleInputChange}
            options={options}
            formatErrorObject={formatErrorObject}
            isMobile={isMobile}
            errors={errors}
          />
        </Grid2>

        <Grid2 gridArea={gridStyle.contact}>
          <DebtorFormSections.Contact
            values={values}
            handleInputChange={handleInputChange}
            options={options}
            formatErrorObject={formatErrorObject}
            isMobile={isMobile}
            isCreate={isCreate}
            errors={errors}
          />
        </Grid2>
        <Grid2 gridArea={gridStyle.references}>
          <DebtorFormSections.References
            values={values}
            handleInputChange={handleInputChange}
            options={options}
            formatErrorObject={formatErrorObject}
            isMobile={isMobile}
            errors={errors}
          />
        </Grid2>
        {false && !isCreate && (
          <Grid2 gridArea={gridStyle.limit}>
            <DebtorFormSections.Limit
              values={values}
              handleInputChange={handleInputChange}
              options={options}
              formatErrorObject={formatErrorObject}
              isMobile={isMobile}
              errors={errors}
            />
          </Grid2>
        )}
      </Grid2>

      <FormFields.Button
        type="submit"
        label={t(`FORM.SUBMIT.${isCreate ? 'create' : 'update'}`)}
        fullWidth
        sx={{ mt: 2 }}
      />
    </Form>
  );
}

function getProfileFromConfig(debtorProfile?: UserProfile) {
  var ProfileFormConfig = getDebtorConfig();

  if (debtorProfile) {
    for (var field in ProfileFormConfig) {
      if (field in debtorProfile) {
        ProfileFormConfig[field].default =
          debtorProfile[field as keyof UserProfile];
      }
    }
    ProfileFormConfig.emailConfirmation.default = debtorProfile.email;
    ProfileFormConfig.city.default = {
      label: debtorProfile.city,
      value: debtorProfile.postCode,
    };
  }

  return ProfileFormConfig;
}

function getDebtorConfig() {
  return {
    // --------- Personal ---------
    gender: {
      default: 'm',
      constraints: ['required'],
      options: [
        {
          label: 'male',
          value: 'm',
        },
        {
          label: 'female',
          value: 'f',
        },
      ],
    },
    language: {
      default: 'fr',
      constraints: ['required'],
      options: [
        {
          label: 'french',
          value: 'fr',
        },
        {
          label: 'english',
          value: 'en',
        },
      ],
    },
    firstName: {
      constraints: ['required'],
      formatter: 'capitalizeWords',
    },
    lastName: {
      constraints: ['required'],
      formatter: 'capitalizeWords',
    },

    birthDate: {
      default: '',
      constraints: ['required', 'ageDate'],
    },

    profession: {
      default: '',
      constraints: ['required'],
    },

    // --------- Contact ---------

    phone: {
      constraints: [
        'required',
        {
          message: {
            name: 'string_equal_length',
            params: {
              length: '10',
            },
          },
          validation: (value: string, values: any) =>
            value === '' || value.length === 10,
        },
      ],
    },
    mobile: {
      constraints: [
        'required',
        {
          message: {
            name: 'string_equal_length',
            params: {
              length: '10',
            },
          },
          validation: (value: string, values: any) =>
            value === '' || value.length === 10,
        },
      ],
    },
    email: {
      constraints: ['required', 'email'],
    },
    emailConfirmation: {
      constraints: [
        'required',
        {
          message: 'email_confirmation',
          validation: (val: string, values: any) =>
            !('email' in values) || val === values.email,
        },
      ],
    },

    // --------- Location ---------
    country: {
      default: Object.keys(Countries)[0],
      constraints: ['required'],
      options: Object.keys(Countries).map((code) => ({
        label: Countries[code].label,
        value: code,
      })),
    },

    city: {
      default: '',
      constraints: ['required'],
      options: [],
    },

    boxNumber: {
      default: '',
      constraints: ['required'],
    },
    box: {
      default: '',
    },

    address: {
      constraints: ['required'],
      formatter: 'capitalizeWords',
    },

    // --------- References ---------

    iban: {
      default: '',
      constraints: ['required', 'iban'],
    },

    externalIdentifier: {
      default: '',
      constraints: ['required', 'externalIdentifier'],
    },
  } as FormFieldsConfig;
}

function getDebtorRequest(values: any) {
  return new DebtorRequest(
    values.gender,
    values.language,
    values.firstName,
    values.lastName,
    values.birthDate,
    values.profession,

    values.country,
    values.city.value,
    values.city.label,

    values.box,
    values.boxNumber,
    values.address,

    values.email,
    values.phone,
    values.mobile,

    values.iban,
    values.externalIdentifier,
  );
}
